/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import Quill from 'quill';
import BstgBlock from './ext/blocks';
import BstgList from './ext/lists';
import BstgImage from './ext/images';
import { BstgLink, ProtocolPrependedLink } from './ext/links';
import { isEmpty } from 'lodash';

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';

/**
 * This component is used to render an HTML editor
 *
 * @vue-prop {String} [eventId] the event ID (if applicable)
 * @vue-prop {Boolean} [images=false] whether to show the image tool button or not
 * @vue-prop {Boolean} [links=false] whether to show the links tool button or not
 * @vue-prop {Boolean} [enableCustomBstgLinks=false'] when false, the default quill link tooltip will show
 * @vue-prop {Boolean} [formats=true] whether to show the texts formats and alligment tool button or not
 * @vue-prop {Boolean} [styles=true] whether to show the texts styles tool button or not
 * @vue-prop {Boolean} [lists=true] whether to show the lists tool button or not
 * @vue-prop {Boolean} [extras=true] whether to show the extras tool button or not
 * @vue-prop {Boolean} [blockquote=false] whether to show the blockquote tool button or not
 *
 * @category ui.components.editors.html
 * @exports HtmlEditor
 */
const __vue_script__ = {
    name: 'HtmlEditor',

    extends: BaseControl,

    props: {
        eventId: {
            type: String,
            default: null
        },

        images: {
            type: Boolean,
            default: false
        },

        links: {
            type: Boolean,
            default: false
        },

        enableCustomBstgLinks: {
            type: Boolean,
            default: false
        },

        formats: {
            type: Boolean,
            default: true
        },

        styles: {
            type: Boolean,
            default: true
        },

        lists: {
            type: Boolean,
            default: true
        },

        extras: {
            type: Boolean,
            default: true
        },

        blockquote: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            content: null,
            editor: null,
            uploading: false,
            ready: this.focused === true,
            options: {
                placeholder: this.placeholder,
                readOnly: this.disabled,
                modules: {
                    toolbar: []
                },
                theme: 'snow'
            }
        };
    },

    watch: {
        focused() {
            this.ready = true;
        },

        disabled() {
            if (this.editor) {
                this.editor.enable(!this.disabled);
            }
        },

        value(newVal) {
            // Only update the content if it's changed from an external source
            // or else it'll act weird when you try to type anything
            if (newVal !== this.content) {
                // Be aware that this call makes Quill set the focus on the editor, which might have some
                // undesired side effects, such as the page scrolling
                this.editor.pasteHTML(newVal);
                this.$emit('restore-locale-selector-focus');
            }
        }
    },

    mounted() {
        this.init();
    },

    beforeDestroy() {
        // Turn off all listeners set on text-change
        this.editor.off('text-change');
    },

    methods: {
        /**
         * Initializes the component
         */
        init() {
            const modals = document.getElementsByClassName('v--modal-box');
            if (modals && modals.length === 1) {
                this.options.bounds = modals[0];
            }

            this.registerExtensions();
            this.buildToolbar();

            // Set initial content that's going to be picked up by Quill
            this.$refs.editor.innerHTML = this.value;
            BstgBlock.trimTrailingNewLines(this.$refs.editor);

            // Create the Quill instance
            this.editor = new Quill(this.$refs.editor, this.options);

            if (this.disabled) {
                this.editor.disable();
            }

            // Register custom handlers
            this.addToolbarHandlers();

            // Setup handler for whenever things change inside Quill
            this.editor.on('editor-change', this.contentChanged);

            // Save any initial content to this.content
            this.setEditorContent();

            // Prevent quill autofocus bug to kick in
            setTimeout(() => this.ready = true, 100);
        },

        /**
         * Register custom extensions on quill editor.
         */
        registerExtensions() {
            Quill.register(BstgBlock, true);

            if (this.lists) {
                Quill.register(BstgList, true);
            }

            const ext = {};

            if (this.images) {
                if (this.eventId) {
                    BstgImage.baseAssetUrl = this.$utils.assets.getEventAssetPath(this.eventId);
                }

                ext['formats/image'] = BstgImage;
            }

            if (this.links) {
                ext['formats/link'] = this.enableCustomBstgLinks ? BstgLink : ProtocolPrependedLink;
            }

            if (!isEmpty(ext)) {
                Quill.register(ext, true);
            }
        },

        /**
         * Populates and build the editor's toolbar starting from the
         * given properties
         */
        buildToolbar() {
            if (this.formats) {
                this.options.modules.toolbar.push([{ header: [1, 2, 3, false] }, { align: [] }]);
            }

            if (this.styles) {
                this.options.modules.toolbar.push(['bold', 'italic', 'underline', 'strike']);
            }

            if (this.lists) {
                this.options.modules.toolbar.push([
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' }
                ]);
            }

            const extraTools = [];

            if (this.extras) {
                extraTools.push({ script: 'sub' }, { script: 'super' });
            }

            if (this.extras || this.blockquote) {
                extraTools.push('blockquote');
            }

            if (!isEmpty(extraTools)) {
                this.options.modules.toolbar.push(extraTools);
            }

            const tools = [];

            if (this.images) {
                tools.push('image');
            }

            if (this.links) {
                tools.push('link');
            }

            if (!isEmpty(tools)) {
                this.options.modules.toolbar.push(tools);
            }
        },

        /**
         * Adds proper handlers based on the given props options.
         */
        addToolbarHandlers() {
            const toolbar = this.editor.getModule('toolbar');

            if (this.links && this.enableCustomBstgLinks) {
                toolbar.addHandler('link', this.insertLink);
            }

            if (this.images) {
                toolbar.addHandler('image', this.insertAsset);
            }
        },

        /**
         * Tries to grab the focus on the editor
         */
        attemptFocus() {
            if (this.focused && this.editor) {
                this.$nextTick(() => this.editor.focus());
            }
        },

        /**
         * Handler that is triggered whenever the content changes
         */
        contentChanged() {
            // Whenever we change anything, update this.content
            this.setEditorContent();

            // Then emit its value as input so we have a working v-model
            // This $emit will be catched up in the watch:value
            // that's why we guard against calling pasteHTML
            // calling that function while we are typing is undesirable
            this.$emit('input', this.content);
        },

        /**
         * Sets the content into the editor
         */
        setEditorContent() {
            const html = this.editor.root.innerHTML;
            const hasContent = html.includes('img') || Boolean(this.editor.getText().trim().length);

            this.content = hasContent ? html : '';
        },

        /**
         * Opens a dialog for inserting or editing a link
         *
         * @param {Boolean} create whether the action is a new insertion or not
         */
        insertLink(create) {
            console.info('[HtmlEditor] Insert link', create);
            // TODO: implement the picker
        },

        /**
         * Opens a dialog for inserting or editing an asset
         *
         * @param {Boolean} create whether the action is a new insertion or not
         */
        insertAsset(create) {
            console.info('[HtmlEditor] Insert asset', create);
            this.$refs['images'].click();
        },

        /**
         * Uploads the provided asset
         */
        async startUpload() {
            try {
                this.uploading = true;
                const files = this.$refs['images'].files || [];
                const { index } = this.editor.getSelection();

                for (const file of files) {
                    const { url } = await this.$services.publicAssets.uploadAsset(this.eventId, file, file.name);
                    this.editor.insertEmbed(index, 'image', { srcset: `${url}, ${url} 2x` });
                }

            } catch (error) {
                console.log('[HtmlEditor] Could not upload assets', error.message || error);
                this.$notify({
                    group: 'flashes',
                    title: this.$t('general.error_occurred'),
                    text: this.$i18n.t('general.forms.html_editor.message.asset_upload_failed').toString(),
                    type: 'error'
                });
            } finally {
                this.uploading = false;
            }
        },

        /**
         * Use this method for labelling purpose
         *
         * @returns {string} the content of the editor as plain text
         */
        getText() {
            return (this.editor.getText() || '').trim();
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('control-decorator',{attrs:{"label":_vm.label,"tip":_vm.tip,"name":_vm.name,"hint":_vm.hint,"errors":_vm.errors,"uid":_vm.uid,"warning":_vm.warning,"dependant":_vm.dependant},scopedSlots:_vm._u([{key:"inside-label",fn:function(ref){return [_vm._t("inside-label")]}}],null,true)},[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.ready),expression:"ready"}],ref:"input",staticClass:"html-editor",class:{disabled: _vm.disabled, 'hide-quill-tooltip': _vm.enableCustomBstgLinks},attrs:{"id":_vm.uid}},[_c('div',{ref:"editor",staticClass:"editor"})]),_c('input',{ref:"images",staticClass:"hidden",attrs:{"type":"file","name":"images","accept":".jpg,.jpeg,.png","disabled":_vm.uploading},on:{"change":_vm.startUpload}})])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "HtmlEditor.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__